export type AvatarBgRemovalResult = {
  imageUrl?: string;
  imageBase64?: string;
  imageMimeType?: string;
};

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function fetchAsDataUrl(url: string) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`fetch image failed: ${res.status}`);
  }
  const blob = await res.blob();
  return blobToDataUrl(blob);
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function isBackgroundPixel(data: Uint8ClampedArray, offset: number) {
  const r = data[offset];
  const g = data[offset + 1];
  const b = data[offset + 2];
  const a = data[offset + 3];
  if (a < 8) return true;
  // A partially-transparent pixel that is reachable from the border is part of
  // the halo an upstream background-removal service leaves around the subject
  // (a ring of uniform semi-alpha, not natural anti-aliasing — the alpha
  // histogram is bimodal, with no soft ramp into the body). The color-based
  // near-white test below misses these when the halo is dark, so they survived
  // as a checkerboard ghost. Since the flood fill only ever visits
  // border-connected pixels, and the subject's interior carries no semi-alpha,
  // treating any non-solid pixel as background here can't eat the character.
  if (a < 250) return true;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const avg = (r + g + b) / 3;
  return avg >= 228 && max - min <= 28;
}

export async function matteWhiteBackgroundToTransparentPng(src: string) {
  const img = await loadImage(src);
  const maxDim = 1024;
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('canvas unavailable');

  ctx.drawImage(img, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const { data } = imageData;
  const total = width * height;
  const visited = new Uint8Array(total);
  const queue = new Uint32Array(total);
  let head = 0;
  let tail = 0;

  const tryPush = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return;
    const idx = y * width + x;
    if (visited[idx]) return;
    const offset = idx * 4;
    if (!isBackgroundPixel(data, offset)) return;
    visited[idx] = 1;
    queue[tail++] = idx;
  };

  for (let x = 0; x < width; x += 1) {
    tryPush(x, 0);
    tryPush(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    tryPush(0, y);
    tryPush(width - 1, y);
  }

  while (head < tail) {
    const idx = queue[head++];
    const x = idx % width;
    const y = Math.floor(idx / width);
    tryPush(x - 1, y);
    tryPush(x + 1, y);
    tryPush(x, y - 1);
    tryPush(x, y + 1);
  }

  // Every pixel reached by the flood fill is connected background, so clear it
  // to FULLY transparent. (The previous "soften by brightness" step used a fade
  // threshold of 245 that was higher than the 228 background-detection
  // threshold, so light-gray background pixels in the 228–245 range kept a
  // partial alpha and rendered as a checkerboard ghost over dark scenes.)
  for (let idx = 0; idx < total; idx += 1) {
    if (!visited[idx]) continue;
    data[idx * 4 + 3] = 0;
  }

  // Feather the one-pixel seam between kept foreground and cleared background so
  // the cutout keeps a soft anti-aliased edge instead of a hard jagged outline.
  // A foreground pixel that touches a cleared background pixel gets its alpha
  // eased down toward that boundary.
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x;
      if (visited[idx]) continue;
      const offset = idx * 4;
      if (data[offset + 3] === 0) continue;
      const touchesBg =
        (x > 0 && visited[idx - 1]) ||
        (x < width - 1 && visited[idx + 1]) ||
        (y > 0 && visited[idx - width]) ||
        (y < height - 1 && visited[idx + width]);
      if (touchesBg) {
        data[offset + 3] = Math.round(data[offset + 3] * 0.5);
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}

function drawToCanvas(img: HTMLImageElement, maxDim = 1024) {
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('canvas unavailable');
  ctx.drawImage(img, 0, 0, width, height);
  return { canvas, ctx, width, height };
}

// Tight opaque bounding box of the subject over pixels above the alpha
// threshold. Returned in the canvas's own pixel space.
function opaqueBounds(data: Uint8ClampedArray, width: number, height: number, thr = 40) {
  let top = -1;
  let bottom = -1;
  let left = width;
  let right = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > thr) {
        if (top < 0) top = y;
        bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  if (top < 0) return { top: 0, left: 0, w: width, h: height, cx: width / 2 };
  return { top, left, w: right - left + 1, h: bottom - top + 1, cx: (left + right + 1) / 2 };
}

// The three frames (neutral / mouth-open / eyes-closed) are each an independent
// Ark edit, so even when derived from one source they can differ in (a) how much
// the subject is scaled and (b) how much transparent margin the cutout leaves —
// i.e. the subject fills a DIFFERENT FRACTION of each frame's canvas. When the
// portrait stacks them with object-fit: contain, `contain` normalizes by the
// CANVAS (padding included), so a different subject-to-canvas fraction renders
// the subject at a different size — the "body pulses when talking/blinking".
//
// We fix this by mapping EACH frame (neutral included) onto ONE fixed canvas
// with the subject placed in ONE fixed target box: the frame's opaque bounding
// box is scaled so its HEIGHT equals a fixed fraction of the canvas (height is
// stable across mouth/eye changes and far less sensitive to hair than width),
// top-aligned to a fixed margin and horizontally centered. The mapping depends
// ONLY on that frame's own bounding box and these fixed constants — never on
// another frame — so every frame can be normalized independently yet still comes
// out with the exact same dimensions and subject box. No frame is an anchor and
// there is NO clamp (an earlier anchor-based variant clamped the toward-anchor
// scale to 0.8–1.25 and silently under-corrected when Ark's per-frame reframing
// exceeded ±25%, leaving the body pulsing). object-fit: contain then renders all
// three identically. Returns the original frame untouched if anything fails.
const NORMALIZE_CANVAS = 1024; // square output; PortraitAvatar uses contain + top-center
const NORMALIZE_SUBJECT_HEIGHT_RATIO = 0.92; // subject height ÷ canvas height
const NORMALIZE_TOP_MARGIN_RATIO = 0.04; // transparent margin above the subject

async function loadNormalizeInput(src: string) {
  const img = await loadImage(src);
  const { canvas, ctx, width, height } = drawToCanvas(img);
  const box = opaqueBounds(ctx.getImageData(0, 0, width, height).data, width, height);
  return { canvas, width, height, box };
}

// PER-FRAME vertical alignment. Each frame (neutral / mouth / eyes) is mapped
// using ITS OWN opaque bounding box: the box height is scaled to the fixed
// target height and its top is pinned to the fixed top margin. Because every
// frame is pinned to the SAME targetH / targetTop, all three come out with the
// EXACT same subject height and head-top position — the vertical dimension is
// mathematically identical across frames, so the head can no longer pulse
// up/down ("向上纵深").
//
// Why not share ONE box across frames? An earlier variant captured the neutral
// box and reused it for the two expression frames to pin WIDTH too. But the
// three Ark frames are independent generations and Ark draws the body at a
// slightly different height each time (neutral 82.0% vs frames 83.x% of canvas).
// Applying neutral's scale/offset to a taller body pushed the head UP and made
// the subject TALLER (measured: height 92.2 / 93.8 / 94.3, top 3.9 / 2.3 / 2.1)
// — i.e. sharing the box AMPLIFIED Ark's height difference into visible vertical
// jitter. Per-frame alignment instead forces every frame's own box to the fixed
// target, so height+top are locked. Width may then vary by ~1pt with each
// frame's aspect ratio, but that is far less visible than the head bobbing and
// the prompt-symmetry fix already keeps widths tight.
export async function normalizeFrameToUniformCanvas(src: string): Promise<string> {
  if (!src) return src;
  try {
    const targetH = NORMALIZE_CANVAS * NORMALIZE_SUBJECT_HEIGHT_RATIO;
    const targetTop = NORMALIZE_CANVAS * NORMALIZE_TOP_MARGIN_RATIO;
    const targetCx = NORMALIZE_CANVAS / 2;
    const { canvas, width, height, box } = await loadNormalizeInput(src);
    if (box.h <= 0) return src;
    // Scale this frame's own box height to the fixed target height.
    const scale = targetH / box.h;
    // Place the scaled box: its top at targetTop, its center-x at targetCx.
    const dx = targetCx - box.cx * scale;
    const dy = targetTop - box.top * scale;
    const out = document.createElement('canvas');
    out.width = NORMALIZE_CANVAS;
    out.height = NORMALIZE_CANVAS;
    const outCtx = out.getContext('2d');
    if (!outCtx) return src;
    outCtx.drawImage(canvas, 0, 0, width, height, dx, dy, width * scale, height * scale);
    return out.toDataURL('image/png');
  } catch {
    return src;
  }
}

export async function resolveAvatarRemovalResult(
  result: AvatarBgRemovalResult,
  fallbackDataUrl: string
) {
  const sourceDataUrl = result.imageBase64
    ? `data:${result.imageMimeType || 'image/jpeg'};base64,${result.imageBase64}`
    : result.imageUrl
    ? await fetchAsDataUrl(result.imageUrl)
    : fallbackDataUrl;

  return matteWhiteBackgroundToTransparentPng(sourceDataUrl);
}

export type MatteKeyColor = 'green' | 'magenta';

// Pick the solid plate color Ark should paint behind the subject so a color-key
// can lift it. We offer two maximally-separated hues — pure green (0,255,0) and
// pure magenta (255,0,255) — and choose whichever the subject's OWN colors sit
// FARTHEST from, so the subject can never collide with the plate (a green-clad
// subject gets magenta, a magenta/pink subject gets green). We sample the matted
// subject's opaque pixels and score how "green-like" vs "magenta-like" the
// subject is; the plate is the opposite of the subject's dominant leaning.
export async function pickMatteKeyColor(src: string): Promise<MatteKeyColor> {
  if (!src) return 'green';
  try {
    const img = await loadImage(src);
    const { ctx, width, height } = drawToCanvas(img, 512);
    const { data } = ctx.getImageData(0, 0, width, height);
    const total = width * height;
    let subjectGreenAffinity = 0; // Σ how green-dominant the subject pixels are
    let subjectMagentaAffinity = 0; // Σ how magenta-dominant they are
    let counted = 0;
    for (let i = 0; i < total; i += 1) {
      const o = i * 4;
      if (data[o + 3] < 200) continue; // subject (opaque) pixels only
      const r = data[o];
      const g = data[o + 1];
      const b = data[o + 2];
      counted += 1;
      // Green affinity: green channel exceeds the red/blue it would collide with.
      subjectGreenAffinity += Math.max(0, g - Math.max(r, b));
      // Magenta affinity: red & blue both exceed green (magenta = r+b, no g).
      subjectMagentaAffinity += Math.max(0, Math.min(r, b) - g);
    }
    if (!counted) return 'green';
    // Put the PLATE opposite the subject's dominant leaning: if the subject is
    // more green-like, key on magenta, and vice-versa.
    return subjectGreenAffinity >= subjectMagentaAffinity ? 'magenta' : 'green';
  } catch {
    return 'green';
  }
}

// Reference RGB for each key color, kept for callers that want to preview or
// composite against the exact plate the server was asked to paint.
export const MATTE_KEY_RGB: Record<MatteKeyColor, [number, number, number]> = {
  green: [0, 255, 0],
  magenta: [255, 0, 255],
};

// Deterministic chroma-key matte for the SOLID-KEY pipeline. Ark paints the
// subject on a uniform green/magenta plate; here we lift that plate to full
// transparency. Unlike the connectivity flood-fill, a color-key reaches
// background trapped inside the silhouette (arm gaps) AND doesn't nibble
// light/hair edges that merely touch the plate, because the decision is per-pixel
// on hue distance, not spatial reachability.
//
// Pipeline per pixel:
//  1) sample the true plate color from the four corners (Ark's plate is uniform
//     but not always exactly 0,255,0), so the key adapts to the actual output.
//  2) score "plate-ness" as the dominance of the key's active channels over the
//     subject's — for green that's g − max(r,b); for magenta min(r,b) − g. A soft
//     ramp between two thresholds feathers the boundary instead of a hard cut.
//  3) despill: pull the over-saturated key channel(s) back toward luma on kept
//     edge pixels so no green/magenta rim survives.
//
// A separability guard: if too little of the frame keys out (subject fills nearly
// the whole canvas), the plate wasn't distinct — the caller falls back.
export async function colorKeyMatte(
  src: string,
  keyColor: MatteKeyColor
): Promise<{ dataUrl: string; keyedFraction: number }> {
  const img = await loadImage(src);
  const { canvas, ctx, width, height } = drawToCanvas(img, 1024);
  const imageData = ctx.getImageData(0, 0, width, height);
  const { data } = imageData;
  const total = width * height;

  // Sample the actual plate color from the four corners (median-ish average).
  const corners = [
    0,
    (width - 1) * 4,
    (height - 1) * width * 4,
    ((height - 1) * width + (width - 1)) * 4,
  ];
  let pr = 0;
  let pg = 0;
  let pb = 0;
  for (const c of corners) {
    pr += data[c];
    pg += data[c + 1];
    pb += data[c + 2];
  }
  pr /= corners.length;
  pg /= corners.length;
  pb /= corners.length;

  const isGreen = keyColor === 'green';
  // Plate-ness score of the sampled corner: used to normalize the ramp so a full
  // plate pixel keys to ~1 regardless of how saturated Ark rendered it.
  const plateScore = isGreen ? pg - Math.max(pr, pb) : Math.min(pr, pb) - pg;
  // Guard against a washed-out plate: if corners aren't clearly the key color,
  // there's nothing safe to key — signal a full-frame fallback.
  if (plateScore < 40) {
    return { dataUrl: canvas.toDataURL('image/png'), keyedFraction: 0 };
  }

  // Ramp: pixels scoring >= HIGH are pure plate (alpha 0); <= LOW are pure
  // subject (alpha kept); between them the alpha eases for a soft edge.
  const HIGH = plateScore * 0.72;
  const LOW = plateScore * 0.28;
  // Below LOW the alpha is fully kept, but a thin ring of OPAQUE pixels right
  // against the subject still carries a faint plate tint (that's the halo). We
  // strip color (not alpha) down to this floor so the rim goes neutral, while
  // keeping the floor high enough to stay clear of true subject hues like
  // reddish lips (which score only slightly positive on a magenta plate).
  const DESPILL_FLOOR = plateScore * 0.16;
  let keyed = 0;

  for (let i = 0; i < total; i += 1) {
    const o = i * 4;
    const r = data[o];
    const g = data[o + 1];
    const b = data[o + 2];
    const score = isGreen ? g - Math.max(r, b) : Math.min(r, b) - g;
    let keyAmount: number; // 1 = fully background, 0 = fully subject
    if (score >= HIGH) keyAmount = 1;
    else if (score <= LOW) keyAmount = 0;
    else keyAmount = (score - LOW) / (HIGH - LOW);

    if (keyAmount >= 1) {
      data[o + 3] = 0;
      keyed += 1;
      continue;
    }
    if (keyAmount > 0) {
      data[o + 3] = Math.round(data[o + 3] * (1 - keyAmount));
    }
    // Despill any pixel still leaning toward the plate (soft edge OR the faint
    // opaque halo ring above DESPILL_FLOOR), clamping the key channel(s) down to
    // the subject channel(s) so no colored fringe survives. Pixels below the
    // floor are genuine subject (green sweater, reddish lips) and are left
    // untouched — a partial pull would either leave a halo or tint the subject.
    if (score > DESPILL_FLOOR) {
      if (isGreen) {
        const cap = Math.max(r, b);
        if (g > cap) data[o + 1] = cap;
      } else {
        // Magenta = red + blue; pull whichever of r/b overshoots green down to
        // green so no pink/magenta fringe survives.
        if (r > g) data[o] = g;
        if (b > g) data[o + 2] = g;
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return { dataUrl: canvas.toDataURL('image/png'), keyedFraction: total ? keyed / total : 0 };
}

// Convenience: given the key color the server used, key the frame out and fall
// back to the flood-fill matte if the plate wasn't separable enough (guard).
export async function colorKeyOrFallback(src: string, keyColor: MatteKeyColor): Promise<string> {
  try {
    const { dataUrl, keyedFraction } = await colorKeyMatte(src, keyColor);
    // A real portrait plate keys out a large chunk of the canvas. If almost
    // nothing keyed, the frame wasn't on the expected plate — fall back.
    if (keyedFraction >= 0.12) return dataUrl;
  } catch {
    // fall through
  }
  return matteWhiteBackgroundToTransparentPng(src);
}
